import type { BlockingMapSnapshot } from "../types/explorer";

export interface MapDailyStats {
  z500Raw: number | null;
  z500MeanAbsoluteAnomaly: number | null;
  gradCam: number | null;
  t2mMaxC: number | null;
}

export type MapPointValues = [string, string, string, string];

function finiteValue(field: number[][] | null | undefined, row: number, col: number) {
  const value = field?.[row]?.[col];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function displayValue(
  value: number | null,
  digits: number,
  signed = false,
): string {
  if (value === null) return "n/a";
  const formatted = value.toFixed(digits);
  return signed && value > 0 ? `+${formatted}` : formatted;
}

/** Return a cosine-latitude-weighted mean over all finite grid cells. */
export function fieldMean(
  field: number[][] | null | undefined,
  lats?: number[],
): number | null {
  if (!field?.length) return null;
  let weightedTotal = 0;
  let totalWeight = 0;
  field.forEach((row, rowIndex) => {
    const latitude = lats?.[rowIndex];
    const weight =
      typeof latitude === "number"
        ? Math.max(Math.cos((latitude * Math.PI) / 180), 0)
        : 1;
    row.forEach((value) => {
      if (!Number.isFinite(value)) return;
      weightedTotal += value * weight;
      totalWeight += weight;
    });
  });
  return totalWeight > 0 ? weightedTotal / totalWeight : null;
}

/** Return the cosine-latitude-weighted mean absolute field magnitude. */
export function fieldMeanAbsolute(
  field: number[][] | null | undefined,
  lats?: number[],
): number | null {
  return fieldMean(
    field?.map((row) => row.map((value) => Math.abs(value))),
    lats,
  );
}

/** Calculate the displayed European-domain mean for every map field. */
export function mapDailyStats(snapshot: BlockingMapSnapshot): MapDailyStats {
  return {
    z500Raw: fieldMean(snapshot.z500Raw, snapshot.lats),
    z500MeanAbsoluteAnomaly: fieldMeanAbsolute(snapshot.z500, snapshot.lats),
    gradCam: fieldMean(snapshot.blockingProbability, snapshot.lats),
    t2mMaxC: fieldMean(snapshot.t2mMaxC, snapshot.lats),
  };
}

/** Align all point values into Plotly customdata for one shared tooltip. */
export function buildMapCustomData(
  snapshot: BlockingMapSnapshot | null,
): MapPointValues[][] {
  if (!snapshot) return [];
  return snapshot.z500.map((row, rowIndex) =>
    row.map((_value, colIndex) => {
      const z500Raw = finiteValue(snapshot.z500Raw, rowIndex, colIndex);
      const z500Anomaly = finiteValue(snapshot.z500, rowIndex, colIndex);
      const gradCam = finiteValue(
        snapshot.blockingProbability,
        rowIndex,
        colIndex,
      );
      const temperature = finiteValue(snapshot.t2mMaxC, rowIndex, colIndex);
      return [
        displayValue(z500Raw, 0),
        displayValue(z500Anomaly, 1, true),
        displayValue(gradCam === null ? null : gradCam * 100, 0),
        displayValue(temperature, 1),
      ];
    }),
  );
}

export const MAP_HOVER_TEMPLATE =
  "<b>%{y:.0f}° lat · %{x:.0f}° lon</b><br><br>" +
  "Z500&nbsp;&nbsp;<b>%{customdata[0]} gpm</b><br>" +
  "Z500*&nbsp;&nbsp;<b>%{customdata[1]} gpm</b><br>" +
  "Grad-CAM&nbsp;&nbsp;<b>%{customdata[2]}%</b><br>" +
  "Daily max T2m&nbsp;&nbsp;<b>%{customdata[3]} °C</b><extra></extra>";
